export const FEATURE_FLAG_KEYS = [
  "ordersReaderParser",
  "speechNormalization",
  "ordersReaderModule",
  "speechOutput",
  "preflight",
  "delivery",
  "trace",
  "gridRecognition",
  "gridRegistration",
  "automaticOrders",
  "estimatedGrids",
  "eyesUp",
  "questions",
  "diagnostics",
  "appUpdates",
  "phaseWalk",
  "radioProcedure",
  "battleProcedure",
  "tacticalSimulation",
] as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];
export type FeatureFlagSnapshot = Readonly<Record<FeatureFlagKey, boolean>>;

export const DEFAULT_FEATURE_FLAGS: FeatureFlagSnapshot = Object.freeze({
  ordersReaderParser: true,
  speechNormalization: true,
  ordersReaderModule: true,
  speechOutput: true,
  preflight: true,
  delivery: true,
  trace: true,
  gridRecognition: true,
  // Ruling 22, 18 September 2026: manual four-point grid registration is out
  // of iteration 1 and has stayed parked in every block since. Off, and the
  // control that offered it says so rather than vanishing. It used to be
  // gated on nothing: the workflow decision offers it whenever registration
  // is not accepted, and a parked registration is never accepted, so it
  // appeared on every document carrying a grid blank.
  gridRegistration: false,
  automaticOrders: false,
  // Ruling 31, 18 September 2026. Off: with it off a low-confidence grid
  // resolves nothing, exactly as before. On, an estimate may fill a blank —
  // and every use of it is announced, because an estimate that reaches the
  // trainee unmarked teaches him a false grid.
  estimatedGrids: false,
  // Ruling 59, 14 October 2026. On: the pens sequence exists now. Doc 03
  // section 6.1 fixes it and doc 03 section 6.2 says the detailed scheme of
  // manoeuvre is not in the document at all, so with this off the cue had
  // nothing to introduce and the narratives were never spoken. Turned on
  // after 59a to 59c passed the smoke on Brave, which section 59f requires.
  eyesUp: true,
  // Ruling 60, 8 October 2026. On: the phase moved out of pre-flight to
  // after COMDSIGS, which is doc 04 section 12 as written, so it now asks
  // him about orders he has actually heard. The bank builds from the card
  // itself and needs no setup.
  questions: true,
  // Ruling 50, 7 October 2026. On: doc 02 section 4 made the event log and
  // the send-a-bug export binding, and they are how every finding after
  // this one gets diagnosed. A diagnostic the trainee cannot reach is the
  // same as no diagnostic at all.
  diagnostics: true,
  // R1 Step 15. On: the offline install is how the tool reaches the field, and
  // acceptance requires an offline run after first load.
  appUpdates: true,
  // Ruling 59e, 14 October 2026. On: Ruling 59's interlude is the place the
  // walk was always meant to run, and until the interlude existed nothing in
  // the delivery path reached this at all. Turned on only because 59e's walk
  // passes the smoke on Brave: the pointer runs at the phase narrative, and
  // does NOT run at the phase name in the general scheme where he is still
  // writing.
  phaseWalk: true,
  radioProcedure: false,
  // Ruling 58d, 8 October 2026. Off until R4.0 is redesigned. R4.0 captures
  // typed work, and the trainee writes on paper in his proforma as he will
  // on course — so the module asks him for something he will never give it.
  // Its code stays; only the flag is off.
  battleProcedure: false,
  tacticalSimulation: false,
});

export interface FeatureFlagService {
  isEnabled(key: FeatureFlagKey): boolean;
  snapshot(): FeatureFlagSnapshot;
}

export function createFeatureFlagService(
  overrides: Partial<Record<FeatureFlagKey, boolean>> = {},
): FeatureFlagService {
  const flags: FeatureFlagSnapshot = Object.freeze({
    ...DEFAULT_FEATURE_FLAGS,
    ...overrides,
  });

  return {
    isEnabled: (key) => flags[key],
    snapshot: () => ({ ...flags }),
  };
}
